import { fromAssigneeHistoryDataList } from './AssigneeHistoryData.js';
import { fromCodeSuggestDataList } from './CodeSuggestData.js';
import { fromCommentDataList } from './CommentData.js';
import { fromContentProcessingErrorData } from './ContentProcessingErrorData.js';
import { fromDocumentDetailsData } from './DocumentDetailsData.js';
import { DocumentStatus } from './DocumentStatus.js';
import { fromExistingPatientCodesData } from './ExistingPatientCodesData.js';
import { fromLmcContractViolationTypeList } from './LmcContractViolationType.js';
import { fromMeasurementDataList } from './MeasurementData.js';
import { fromNewCodeDataList } from './NewCodeData.js';
import { fromOutcomeTaskDataList } from './OutcomeTaskData.js';
import { fromPotentialExistingCodeDataList } from './PotentialExistingCodeData.js';
import {
  asBoolean,
  asInstant,
  assignIfPresent,
  mapStringList,
  optionalEnum,
  optionalNumber,
  optionalString,
  requireEnum,
  requireNumber,
  requireObject,
  requireString,
} from './parse.js';

const TYPE_NAME = 'PatientDocumentData';

/**
 * Mirrors com.medprompt.document.FileType
 */
export const FileType = Object.freeze({
  PDF: 'PDF',
  JPEG: 'JPEG',
  PNG: 'PNG',
});

/**
 * Mirrors com.medprompt.document.risk.RiskCategory
 */
export const RiskCategory = Object.freeze({
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
});

const optionalObject = (mapper) => (value) => (value == null ? null : mapper(value));

/** Keyed by DocumentContentType name. */
function fromContentTypeToError(raw) {
  const source = requireObject(raw, `${TYPE_NAME}.contentTypeToError`);
  const errors = {};
  Object.entries(source).forEach(([contentType, error]) => {
    errors[contentType] = fromContentProcessingErrorData(error);
  });
  return errors;
}

/**
 * Mirrors com.medprompt.document.PatientDocumentData (`@JsonInclude(NON_NULL)`).
 * Optional fields Jackson left out stay absent; see `assignIfPresent`.
 *
 * @typedef {object} PatientDocumentData
 * @property {string} documentId
 * @property {string} fileType
 * @property {number} timestamp
 * @property {number|null} pageCount
 * @property {boolean} isInvalid
 * @property {boolean} isFiledAway
 * @property {string} [path]
 * @property {string|null} [nhsId]
 * @property {string|null} [patientId]
 * @property {import('./DocumentDetailsData.js').DocumentDetailsData|null} [documentDetails]
 * @property {string|null} [summary]
 * @property {string|null} [briefSummary]
 * @property {import('./OutcomeTaskData.js').OutcomeTaskData[]} [outcomeTasks]
 * @property {import('./ExistingPatientCodesData.js').ExistingPatientCodesData|null} [existingPatientCodes]
 * @property {import('./CodeSuggestData.js').CodeSuggestData[]} [newCodeSuggests]
 * @property {import('./PotentialExistingCodeData.js').PotentialExistingCodeData[]} [potentialExistingCodes]
 * @property {import('./NewCodeData.js').NewCodeData[]} [frequentCodes]
 * @property {import('./MeasurementData.js').MeasurementData[]} [measurements]
 * @property {string|null} [riskCategory]
 * @property {boolean} [isMultiplePatients]
 * @property {string[]} [lmcContractViolations]
 * @property {Object<string, import('./ContentProcessingErrorData.js').ContentProcessingErrorData>} [contentTypeToError]
 * @property {string|null} [documentTitle]
 * @property {string|null} [overrideTitle]
 * @property {import('./CommentData.js').CommentData[]} [comments]
 * @property {string|null} [assigneeId]
 * @property {import('./AssigneeHistoryData.js').AssigneeHistoryData[]} [assigneeHistory]
 * @property {string[]} [commIds]
 * @property {string|null} [senderOdsCode]
 * @property {string|null} [senderMeshMailboxId]
 * @property {string|null} [senderEmail]
 * @property {string|null} [status]
 * @property {number|string|null} [filedAwayAt]
 */

/**
 * @param {object} raw
 * @returns {PatientDocumentData}
 */
export function fromPatientDocumentData(raw) {
  const source = requireObject(raw, TYPE_NAME);
  const document = {
    documentId: requireString(source, 'documentId', TYPE_NAME),
    fileType: requireEnum(source.fileType, FileType, TYPE_NAME, 'fileType'),
    timestamp: requireNumber(source, 'timestamp', TYPE_NAME),
    pageCount: optionalNumber(source.pageCount),
    isInvalid: asBoolean(source.isInvalid),
    isFiledAway: asBoolean(source.isFiledAway),
  };

  assignIfPresent(document, source, 'path', optionalString);
  assignIfPresent(document, source, 'nhsId', optionalString);
  assignIfPresent(document, source, 'patientId', optionalString);
  assignIfPresent(document, source, 'documentDetails', optionalObject(fromDocumentDetailsData));
  assignIfPresent(document, source, 'summary', optionalString);
  assignIfPresent(document, source, 'briefSummary', optionalString);
  assignIfPresent(document, source, 'outcomeTasks', fromOutcomeTaskDataList);
  assignIfPresent(document, source, 'existingPatientCodes', optionalObject(fromExistingPatientCodesData));
  assignIfPresent(document, source, 'newCodeSuggests', fromCodeSuggestDataList);
  assignIfPresent(document, source, 'potentialExistingCodes', fromPotentialExistingCodeDataList);
  assignIfPresent(document, source, 'frequentCodes', fromNewCodeDataList);
  assignIfPresent(document, source, 'measurements', fromMeasurementDataList);
  assignIfPresent(document, source, 'riskCategory', (value) => (
    optionalEnum(value, RiskCategory, TYPE_NAME, 'riskCategory')
  ));
  assignIfPresent(document, source, 'isMultiplePatients', asBoolean);
  assignIfPresent(document, source, 'lmcContractViolations', (value) => (
    fromLmcContractViolationTypeList(value, TYPE_NAME)
  ));
  assignIfPresent(document, source, 'contentTypeToError', optionalObject(fromContentTypeToError));
  assignIfPresent(document, source, 'documentTitle', optionalString);
  assignIfPresent(document, source, 'overrideTitle', optionalString);
  assignIfPresent(document, source, 'comments', fromCommentDataList);
  assignIfPresent(document, source, 'assigneeId', optionalString);
  assignIfPresent(document, source, 'assigneeHistory', fromAssigneeHistoryDataList);
  assignIfPresent(document, source, 'commIds', (value) => mapStringList(value, `${TYPE_NAME}.commIds`));
  assignIfPresent(document, source, 'senderOdsCode', optionalString);
  assignIfPresent(document, source, 'senderMeshMailboxId', optionalString);
  assignIfPresent(document, source, 'senderEmail', optionalString);
  assignIfPresent(document, source, 'status', (value) => (
    optionalEnum(value, DocumentStatus, TYPE_NAME, 'status')
  ));
  assignIfPresent(document, source, 'filedAwayAt', asInstant);

  return document;
}
